// Tooltip content (lib/tip.js) for a unit and for a team, shared by the bracket, the spotlight and the team panels.
// With game data (`gd`) a unit's tip is the old bracket hover's whole story: stats, evasion, each weapon's and ability's
// damage (lib/unitOutput.js), elements, gear statuses, and zodiac against the foes and its own team (non-neutral only).
// Callers pass the grades they already cached (unitGrade / teamGrade), so hovering costs nothing new.
import { topParts } from './unitGrade.js';
import { unitOutput } from './unitOutput.js';
import { zodiacCompat } from './zodiac.js';
import { unitInfo, elementChips } from '../components/spotlight/unitInfo.js';

const ZODIAC_COLOR = { Best: '#8fe0a0', Good: '#8fe0a0', Bad: '#f08a7a', Worst: '#f08a7a' };
const sign = (n) => (n > 0 ? '+' : '') + n;

export function unitTip(u, { team = '', color = null, grade = null, foot = null, gd = null, foes = null, foeLabel = '', mates = null } = {}) {
  const monster = u.Gender === 'Monster';
  const out = gd ? unitOutput(u, gd) : null;
  const dmg = (w) => w.name + (w.out ? ' (' + w.out + ')' : '');
  const gear = monster ? null : out ? out.weapons.map(dmg).join(' + ') : [u.Mainhand, u.Offhand].filter(Boolean).join(' + ');
  const armor = monster ? null : [u.Head, u.Armor].filter(Boolean).join(' · ');
  const chips = [];
  if (grade && grade.parts) {
    const { up, down } = topParts(grade.parts, 2);
    for (const p of up) chips.push({ text: p.label + ' ' + sign(p.contribution), color: '#8fe0a0' });
    for (const p of down) chips.push({ text: p.label + ' ' + sign(p.contribution), color: '#f08a7a' });
  }
  let statLine = null, evadeLine = null, elementLine = null, statusLine = null;
  if (gd) {
    const info = unitInfo(u, gd), st = info.stats;
    statLine = `HP ${st.hp} · MP ${st.mp} · PA ${st.pa} · MA ${st.ma} · Sp ${st.speed} · Mv ${st.move} · Jp ${st.jump}`;
    evadeLine = `F ${info.evade.front}% · S ${info.evade.side}% · B ${info.evade.back}% · Magic ${info.evade.magic}%`;
    elementLine = elementChips(info.elements).map((c) => c.label + ' ' + c.el).join(' · ') || null;
    const perm = new Set(), init = new Set();
    for (const it of Object.values(info.equipped)) { for (const x of it.permstatus || []) perm.add(x.name); for (const x of it.initstatus || []) init.add(x.name); }
    statusLine = [perm.size ? 'Always ' + [...perm].join(', ') : '', init.size ? 'Starts with ' + [...init].join(', ') : ''].filter(Boolean).join(' · ') || null;
  }
  const zodiac = (list, skip) => (list || []).filter((t) => !(skip && t === u)).map((t) => {
    const c = zodiacCompat(u.Gender, u.Sign, t.Gender, t.Sign);
    return { text: t.Name + ' ' + c, color: ZODIAC_COLOR[c] || null };
  }).filter((c) => !/ Neutral$/.test(c.text));
  return {
    kicker: [team, u.RaidBoss ? '★ Raid boss' : ''].filter(Boolean).join(' · '),
    title: u.Name,
    color,
    sub: (monster ? 'Monster · ' + u.Class : (u.Gender || '') + ' ' + u.Class) + (u.Sign ? ' · ' + u.Sign : ''),
    rows: [
      ['Grade', grade ? grade.grade + ' · ~' + grade.score + '% expected' : null, grade && grade.color],
      ['Brave / Faith', u.Brave != null ? u.Brave + ' / ' + u.Faith : null],
      ['Stats', statLine],
      ['Evade', evadeLine],
      ['Weapon', gear],
      ['Secondary', u.ActionSkill],
      ['Reaction', u.ReactionSkill],
      ['Support', u.SupportSkill],
      ['Move', u.MoveSkill],
      ['Armor', armor],
      ['Accessory', monster ? null : u.Accessory],
      ['Elements', elementLine, '#f3d68e'],
      ['Gear status', statusLine, '#9fc4e2'],
    ],
    pairs: out ? out.abilities.map((a) => [a.name, a.out]) : null,
    groups: [
      { label: 'Zodiac vs ' + (foeLabel || 'foes'), chips: zodiac(foes, false) },
      { label: 'With team', chips: zodiac(mates, true) },
    ],
    chips,
    lines: out && out.abilities.some((a) => a.out) ? ['Damage vs a 60-Faith target, before evasion, elements and zodiac.'] : [],
    foot,
  };
}

// A team: its grade, and each unit with its own grade.
export function teamTip(label, units, { color = null, tg = null, kicker = 'Team', lines = [], foot = null } = {}) {
  return {
    kicker,
    title: label,
    color,
    rows: [['Team grade', tg ? tg.grade + ' · ~' + tg.score + '% expected' : null, tg && tg.color]],
    chips: (units || []).map((u, i) => {
      const g = tg && tg.units && tg.units[i];
      return { text: u.Name + ' · ' + u.Class + (g ? ' ' + g.grade : ''), color: g ? g.color : null };
    }),
    lines,
    foot,
  };
}
